import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Divider,
  Snackbar,
  Alert,
  ListItemText,
  IconButton,
  Input,
} from '@mui/material';
import { FilterOptions, FilterTitle, FilterSection, ProductList, ProductListItem, ResultsContainer } from './constants';
import CloseIcon from '@mui/icons-material/Close';
import { useProductFinder } from './useProductFinder';
import { ProductFinderProps } from './types';
import { useNavigate } from 'react-router-dom';
export const ProductFinder: React.FC<ProductFinderProps> = ({ open, onClose }) => {
  const { filteredFilters, selectedFilters, onFilterChange, onSearch, onCopy, showCopySuccess, filteredProducts, setShowCopySuccess } =
    useProductFinder();
  const navigate = useNavigate();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Product Finder</DialogTitle>
      <IconButton onClick={onClose} sx={{ position: 'absolute', right: 0, top: 0 }}>
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 0 }}>
        <Box display="flex" gap={2} height="70vh">
          <Box flex={1} overflow="auto" p={2}>
            {filteredFilters.map(filter => (
              <FilterSection key={filter.id}>
                <FilterTitle variant="subtitle1">{filter.label}</FilterTitle>
                <FilterOptions sx={{ paddingLeft: 1.5 }}>
                  {filter.filteredOptions.map(option => (
                    <FormControlLabel
                      key={option}
                      control={<Checkbox checked={selectedFilters[filter.id]?.includes(option)} onChange={() => onFilterChange(filter.id, option)} />}
                      label={option.replace(/_/g, ' ')}
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          textTransform: 'capitalize',
                        },
                      }}
                    />
                  ))}
                </FilterOptions>
              </FilterSection>
            ))}
          </Box>

          <Box flex={1} display="flex" flexDirection="column">
            <Input placeholder="Search" sx={{ marginBottom: 1.5 }} onChange={onSearch} />
            <Divider />
            <ResultsContainer elevation={3} sx={{ flex: 1, borderRadius: 0 }}>
              <Typography variant="h6" gutterBottom sx={{ p: 2 }}>
                Results ({filteredProducts?.length})
              </Typography>
              <ProductList>
                {filteredProducts?.map((product, index) => (
                  <React.Fragment key={product.id || index}>
                    <ProductListItem
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        navigate(`/reference/products/${product.symbol.toUpperCase()}`);
                        onClose();
                      }}
                    >
                      <ListItemText
                        primary={product.symbol.toUpperCase()}
                        secondary={
                          <Box component="span" sx={{ display: 'flex', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {product.full_name}
                            </Typography>
                          </Box>
                        }
                      />
                    </ProductListItem>
                    {index < filteredProducts?.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </ProductList>
            </ResultsContainer>

            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              <Button onClick={onCopy} variant="contained" sx={{ float: 'right' }}>
                Copy Symbols
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <Snackbar
        open={showCopySuccess}
        autoHideDuration={1000}
        onClose={() => setShowCopySuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowCopySuccess(false)} severity="success" sx={{ width: '100%' }}>
          {filteredProducts?.length} symbols copied to clipboard
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
